import { FC } from 'react';
import { useDesktop } from '@utils/hooks/useDesktop';
import { usePaulina } from './PaulinaProvider';
import { PaulinaPill } from './PaulinaPill';
import { PaulinaPanel } from './PaulinaPanel';
import { PaulinaSheet } from './PaulinaSheet';
import { PaulinaNudgeBubble } from './PaulinaNudgeBubble';

/**
 * AgentChatWidget — host de la entrada de Paulina (Opción A). Ya NO renderiza el FAB
 * flotante ni la tarjeta-nudge flotante: orquesta las superficies por VIEWPORT.
 *
 *   DESKTOP → PaulinaPill (entrada nombrada, z-80, oculta si hay pedido) +
 *             PaulinaPanel (chat abierto, z-95, cede ante el toast de pedido) +
 *             PaulinaNudgeBubble (proactividad: speech-bubble, suprimida si hay pedido).
 *   MÓVIL   → PaulinaSheet (bottom-sheet, z-95). La ENTRADA en móvil es el avatar de
 *             Paulina en MobileTopBarV4 (header), que abre este sheet vía el contexto.
 *
 * Toda la lógica (polling, historial namespaceado, hints/nudges, rage-click,
 * telemetría pau_*, navegación por llave ACCION_IR, aislamiento multi-tenant) vive en
 * `usePaulinaChat` y se comparte vía PaulinaProvider; la cesión ante pedidos
 * (`hayPedido`) viene de `usePaulinaShouldYield`. Lo operativo SIEMPRE gana: el toast
 * de pedido (z-99) queda por encima de toda la UI de Paulina (pill z-80, sheet/panel z-95).
 */

export const AgentChatWidget: FC = () => {
  const isDesktop = useDesktop();
  const paulina = usePaulina();

  if (!paulina || !paulina.enabled) return null;

  const {
    open,
    setOpen,
    turns,
    input,
    setInput,
    busy,
    error,
    nudge,
    bottomRef,
    toggleOpen,
    goTo,
    aceptarNudge,
    descartarNudge,
    clearConversation,
    send,
    hayPedido,
  } = paulina;

  // MÓVIL: la entrada es el avatar del header (MobileTopBarV4); aquí solo el sheet.
  if (!isDesktop) {
    if (!open) return null;
    return (
      <PaulinaSheet
        turns={turns}
        input={input}
        setInput={setInput}
        busy={busy}
        error={error}
        bottomRef={bottomRef}
        goTo={goTo}
        send={send}
        setOpen={setOpen}
        clearConversation={clearConversation}
      />
    );
  }

  // DESKTOP: pill (entrada) + panel (chat) + nudge (proactividad).
  return (
    <>
      {open ? (
        <PaulinaPanel
          turns={turns}
          input={input}
          setInput={setInput}
          busy={busy}
          error={error}
          bottomRef={bottomRef}
          goTo={goTo}
          send={send}
          setOpen={setOpen}
          clearConversation={clearConversation}
          hayPedido={hayPedido}
        />
      ) : (
        // Lo operativo gana: la pill se oculta por completo cuando hay pedido entrante.
        !hayPedido && <PaulinaPill hasNudge={Boolean(nudge)} onOpen={toggleOpen} />
      )}

      {/* Proactividad: speech-bubble sobre la pill, suprimida si hay pedido o chat abierto. */}
      {!open && !hayPedido && nudge && (
        <PaulinaNudgeBubble nudge={nudge} onAccept={aceptarNudge} onDismiss={descartarNudge} />
      )}
    </>
  );
};
